import { useEffect, useMemo } from "react";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import "./EmployeeDashboard.css";
import { useParams, useLocation } from "react-router-dom";
import { useState } from "react"; // Import useParams for accessing route parameters
import moment from "moment";
import BlockedSlotManager from "./BlockedSlots/blocked-slots-manager";
import {
  GET_INVENTORY_LIST,
  UPDATE_EMPLOYEE,
  GET_BLOCKED_SLOTS,
  SETTLE_PAYMENTS,
  GET_ORDER_PAYMENTS,
  EMPLOYEE_INVENTORY,
  GET_BOOKINGS,
  ASSIGN_INVENTORY_TO_EMPLOYEE,
  GET_EMPLOYEE_FEEDBACK_ANALYTICS,
  GET_ORDER_FEEDBACK_BY_ORDER
} from "./graphql";
import EmployeeFeedbackDashboard from "./EmployeeFeedbackDashboard";
import SettledPayments from "./SettledPayments";
import UnsettledPayments from "./UnsettledPayments";
import DatewiseInventory from "./DatewiseInventory";
import InventoryTotalSummary from "./CurrentInventories";
import RequestedInventories from "./RequestedInventories";
import AddInventoryForm from "./AddInventoryForm";
import TotalServicesPanel from "./TotalSevices/TotalServicesPanel";
import { GET_EMPLOYEES as GET_EMPLOYEES_FOR_DASHBOARD } from "../../../graphql/userManagement";
import { EmployeeChangeHistory } from "../EmployeeChangeHistoryModal";
import FullFinalSettlement from "./FullFinalSettlement";

const EmployeeDashboard = () => {
  const TAB_CONFIG = {
    services: {
      label: "Services",
      subTabs: [{ key: "totalServices", label: "Total Services" }],
    },
    payments: {
      label: "Payments",
      subTabs: [
        { key: "unsettledPayments", label: "Unsettled" },
        { key: "settledPayments", label: "Settled" },
      ],
    },
    inventory: {
      label: "Inventory",
      subTabs: [
        { key: "inventoryTotal", label: "Current" },
        { key: "inventoryRequested", label: "Requested" },
        { key: "inventoryList", label: "History" },
        { key: "addInventory", label: "Add Inventory" },
      ],
    },
    slots: {
      label: "Slots",
      subTabs: [{ key: "addBlockedSlots", label: "Blocked Slots" }],
    },
    feedback: {
      label: "Feedback",
      subTabs: [{ key: "feedbackAnalytics", label: "Analytics" }],
    },
    // Every change to the record, fines, adjustments, leave balance, questions
    history: {
      label: "History",
      subTabs: [{ key: "employeeHistory", label: "Changes" }],
    },
    // Last pay, cash, kit for someone who left
    settlement: {
      label: "Full & final",
      subTabs: [{ key: "fullFinal", label: "Statement" }],
    },
  };
  const formatTime = (value) => {
    console.log("Formatting time for value:", value);
    if (!value) return "--";
    const d = new Date(value);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ?tab=settlement (links from Staff Desk) opens Full & final
  const startOnSettlement = new URLSearchParams(window.location.search).get("tab") === "settlement";
  const [activeMainTab, setActiveMainTab] = useState(startOnSettlement ? "settlement" : "services");
  const [activeSubTab, setActiveSubTab] = useState(startOnSettlement ? "fullFinal" : "totalServices");

  const currentMainTabConfig = useMemo(
    () => TAB_CONFIG[activeMainTab],
    [activeMainTab]
  );

  const handleMainTabClick = (tabKey) => {
    setActiveMainTab(tabKey);
    // default subtab = first of that group
    const firstSub = TAB_CONFIG[tabKey]?.subTabs?.[0];
    if (firstSub) {
      setActiveSubTab(firstSub.key);
    }
  };

  const handleSubTabClick = (subKey) => {
    setActiveSubTab(subKey);
  };

  const { employeeId } = useParams();

  const [
    handleSubmitInventory,
    { inventorySubmitLoading, inventorySubmitError },
  ] = useMutation(ASSIGN_INVENTORY_TO_EMPLOYEE);
  const [
    settlePayments,
    { settlePaymentsLoading, settlePaymentsError },
  ] = useMutation(SETTLE_PAYMENTS);


  const [getBlockedSlots, { blockedSlotsFetching, blockedSlotsError }] =
    useLazyQuery(GET_BLOCKED_SLOTS);

  const fetchBlockedSlots = async ({ employeeId, date }) => {
    try {
      const res = await getBlockedSlots({
        variables: { data: { employeeId, date } },
        fetchPolicy: "network-only",
      });
      return res?.data?.getBlockedSlots || { blockedSlots: [] };
    } catch (err) {
      console.error("Error fetching blocked slots", err);
      return { blockedSlots: [] };
    }
  };

  const {
    loading: inventroyLoading,
    error: inventroyError,
    data: inventroyData,
  } = useQuery(GET_INVENTORY_LIST);

  const [
    getEmployeeInventory,
    { employeeInventoryFetching, employeeInventoryFetchError },
  ] = useMutation(EMPLOYEE_INVENTORY);

  const [getOrderPayments, { orderPaymentsFetching, orderPaymentsFetchError }] =
    useMutation(GET_ORDER_PAYMENTS);

  const [openDate, setOpenDate] = useState(null);
  const toggleDate = (date) => {
    setOpenDate(openDate === date ? null : date);
  };
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedData, setSelectedData] = useState("totalServices");
  const [searchBookingId, setSearchBookingId] = useState("");
  const [searchId, setSearchId] = useState("");
  const [searchID, setSearchID] = useState("");
  const location = useLocation();
  // Opened from the employee list the row comes with the route; opened by a
  // link (Staff Desk, a bookmark) it is loaded by id
  const { data: employeeLookup } = useQuery(GET_EMPLOYEES_FOR_DASHBOARD, {
    variables: { data: { id: employeeId } },
    skip: Boolean(location.state?.employeeData),
    fetchPolicy: "network-only",
  });
  const employeeData = location.state?.employeeData
    || employeeLookup?.getEmployeelist?.employee?.[0]
    || { id: employeeId };
  const [dataList, setDataList] = useState([]);
  const [inventoryList_collection, setInventoryList] = useState([
    { inventoryName: "" },
  ]);

  const [datewiseInventoryData, setDataWiseInventoryData] = useState([]);
  const [inventoryList, setinventoryList] = useState([]);
  const [requestedInventories, setRequestedInventories] = useState([]);
  const [unsettledPayments, setUnsettledPayments] = useState([]);
  const [setttledPayments, setSettledPayments] = useState([]);
  const [settledCash, setSettledCash] = useState(0);
  const [unsettledCash, setUnsettledCash] = useState(0);
  const [settledOnline, setSettledOnline] = useState(0);

  const [bookingFilter, setBookingFilter] = useState({
    bookingId: "",
    status: null,
    queryStartDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    queryEndDate: moment().format("YYYY-MM-DD"),
    verificationStatus: null,
    selectedDatePreset: "Last 7 Days",
    customStart: null,
    customEnd: null,
  });

  const [openBookingId, setOpenBookingId] = useState(null);
  const toggleBooking = (id) => {
    setOpenBookingId((prev) => (prev === id ? null : id));
  };

  const handleAddInventory = (e) => {
    console.log("handleAddInventory called with data:", e);

    handleSubmitInventory({
      variables: {
        data: e,
      },
    })
      .then((res) => {
        console.log("res is");
        console.log(res);
        const result = res.data.assignInventoryToEmployee;
        if (result.status === "SUCCESS") {
          if (result.warnings?.length) window.alert(result.message);
          window.location.reload();
        } else {
          window.alert(result.message || "Failed to assign inventory.");
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    try {
      let finalDataObj = {};
      finalDataObj.employeeId = employeeId;
      getOrderPayments({
        variables: {
          data: finalDataObj,
        },
      })
        .then((res) => {
          setSettledPayments(
            res.data.getEmployeePayments.settledPaymentsByDate
          );
          setUnsettledPayments(
            res.data.getEmployeePayments.unSettledPaymentsByDate
          );
          setUnsettledCash(res.data.getEmployeePayments.settledCash);
          setSettledCash(res.data.getEmployeePayments.unsettledCash);
          setSettledOnline(res.data.getEmployeePayments.settledOnline);
        })
        .catch((err) => { });
    } catch (err) {
      console.log(err);
    }
  }, []);
  useEffect(() => {
    try {
      let finalDataObj = {};
      finalDataObj.id = employeeId;
      getEmployeeInventory({
        variables: {
          data: finalDataObj,
        },
      })
        .then((res) => {
          setDataWiseInventoryData(
            res.data.getEmployeeInventory.inventoriesByDate
          );
          setinventoryList(res.data.getEmployeeInventory.inventoryList);
          setRequestedInventories(
            res.data.getEmployeeInventory.requestedInventoryCategories
          );
        })
        .catch((err) => { });
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    try {
      const filteredList = inventroyData.getInventory.inventoryList.filter(
        (item) => item.status === "UNASSIGNED"
      );
      console.log("filteredList is", filteredList);

      setDataList(filteredList);

      //extract the data from the respose
    } catch (err) {
      console.log(err);
    }
  }, [inventroyData]);

  // For GET_BOOKINGS
  const [
    getBookings,
    { data: bookingsData, loading: bookingsLoading, error: bookingsError },
  ] = useMutation(GET_BOOKINGS);

  useEffect(() => {
    if (!employeeId) return;

    const filterInput = {
      employeeId,
    };

    if (bookingFilter.queryStartDate) filterInput.queryStartDate = bookingFilter.queryStartDate;
    if (bookingFilter.queryEndDate) filterInput.queryEndDate = bookingFilter.queryEndDate;

    getBookings({
      variables: {
        data: filterInput,
      },
    }).catch((err) => {
      console.error("Error fetching bookings", err);
    });
  }, [employeeId, bookingFilter.queryStartDate, bookingFilter.queryEndDate, getBookings]);

  const rawBookings = bookingsData?.getBookings?.orders ?? [];

  const services = rawBookings;
  // const services = rawBookings.map((service) => ({
  //   id: service?.id ?? "N/A",
  //   price: service?.price ?? 0,
  //   totalPrice: service?.totalPrice ?? 0,
  //   address: service?.address ?? "Not Provided",
  //   bookingDate: service?.bookingDate ?? "Not Available",
  //   paymentType: service?.paymentType ?? "Not Specified",
  //   status: service?.status ?? "Unknown",
  //   timeSlot: service?.timeSlot ?? "Not Available",
  //   totalTime: service?.totalTime ?? "Not Available",
  //   bookingId: service?.bookingId ?? "Not Available",
  //   paymentStatus: service?.paymentStatus ?? "Unknown",
  //   pincode: service?.pincode ?? "",
  //   bookingType: service?.bookingType ?? "",
  //   couponDiscount: service?.couponDiscount ?? 0,
  //   couponCode: service?.couponCode ?? "",
  //   reason: service?.reason ?? "",
  //   confirmedAt: service?.confirmedAt ?? null,
  //   headedToLocation: service?.headedToLocation ?? null,
  //   reachedLocation: service?.reachedLocation ?? null,
  //   serviceStarted: service?.serviceStarted ?? null,
  //   serviceFinished: service?.serviceFinished ?? null,
  //   cancelledAt: service?.cancelledAt ?? null,
  //   serviceScanned: service?.serviceScanned ?? null,
  //   orderDetail:
  //     service?.orderDetail?.map((detail) => ({
  //       id: detail?.id ?? "N/A",
  //       name: detail?.name ?? "Unknown",
  //       price: detail?.price ?? 0,
  //     })) ?? [],
  //   employee: {
  //     id: service?.employee?.id ?? "N/A",
  //     fullName: service?.employee?.fullName ?? "Unknown",
  //   },
  //   user: {
  //     id: service?.user?.id ?? "N/A",
  //     fullName: service?.user?.fullName ?? "Unknown",
  //     contactNumber: service?.user?.contactNumber ?? "",
  //   },
  // }));

  //handle the click on buttons
  const handleButtonClick = (type) => {
    setSelectedData(type);
  };

  // Handle date input changes
  const handleStartDateChange = (e) => setStartDate(e.target.value);
  const handleEndDateChange = (e) => setEndDate(e.target.value);

  // Helper function to check if a date is within the range
  const isWithinDateRange = (date) => {
    const timestamp = new Date(date).getTime();
    const startTimestamp = startDate ? new Date(startDate).getTime() : null;
    const endTimestamp = endDate ? new Date(endDate).getTime() : null;

    if (startTimestamp && timestamp < startTimestamp) return false;
    if (endTimestamp && timestamp > endTimestamp) return false;
    return true;
  };

  const handleApprove = async (id, date, settlementMethod) => {
    settlePayments({
      variables: {
        data: { orderIds: [id], settlementMethod },
      },
    })
      .then((res) => {
        if (res?.data?.settlePayments?.status !== "SUCCESS") {
          console.log(res?.data?.settlePayments?.message);
          return;
        }
        setUnsettledPayments((prev) =>
          prev
            .map((group) => {
              if (group.date !== date) return group;
              return {
                ...group,
                payments: group.payments.filter((p) => p.id !== id),
              };
            })
            .filter((group) => group.payments.length > 0)
        );
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleApproveAll = async (ids, date, settlementMethod) => {
    try {
      const res = await settlePayments({
        variables: {
          data: { orderIds: ids, settlementMethod },
        },
      });

      if (res?.data?.settlePayments?.status !== "SUCCESS") {
        console.log(res?.data?.settlePayments?.message);
        return;
      }

      setUnsettledPayments((prev) =>
        prev
          .map((group) => {
            if (group.date !== date) return group;
            return {
              ...group,
              payments: group.payments.filter((p) => !ids.includes(p.id)),
            };
          })
          .filter((group) => group.payments.length > 0)
      );
    } catch (err) {
      console.error("Error approving all payments", err);
    }
  };

  const filteredTotal = inventoryList.filter((total) => {
    const matchesId = total.id.toLowerCase().includes(searchID.toLowerCase());
    const matchesDate = isWithinDateRange(total.date);
    return matchesId && matchesDate;
  });

  // Handle input change for filter for totalservices
  const handleSearchChange1 = (e) => {
    setSearchBookingId(e.target.value);
  };
  //Handle input change for filter for inventorylist
  const handleSearchChange2 = (e) => {
    setSearchId(e.target.value);
  };
  //Handle input change for filter for inventorytotal
  const handleSearchChange3 = (e) => {
    setSearchID(e.target.value);
  };
  const handleInventoryChange = (index, event) => {
    const { name, value } = event.target;
    const updatedList = [...inventoryList_collection];
    updatedList[index][name] = value;
    setInventoryList(updatedList);
  };

  const handleInventoryPriceChange = (index, event) => {
    const { name, value } = event.target;
    const updatedList = [...inventoryList_collection];
    updatedList[index][name] = parseInt(value);
    setInventoryList(updatedList);
  };

  const handleRemoveRow = (index) => {
    const updatedList = [...inventoryList_collection];
    updatedList.splice(index, 1);
    setInventoryList(updatedList);
  };

  const handleAddRow = () => {
    setInventoryList([...inventoryList_collection, { inventoryName: "" }]);
  };
  // Helper function to display the data as cards
  const renderCards = (dataType) => {
    switch (dataType) {
      case "totalServices":
        return (
          <TotalServicesPanel
            bookingsLoading={bookingsLoading}
            bookingsError={bookingsError}
            services={services}
            employeeData={employeeData}
            bookingFilter={bookingFilter}
            onFilterChange={setBookingFilter}
          />
        );
      // if (bookingsLoading) {
      //   return <Loader />;
      // }

      // if (bookingsError) {
      //   return (
      //     <p className="nodata">
      //       Error loading bookings: {bookingsError.message}
      //     </p>
      //   );
      // }

      // if (services.length === 0) {
      //   return (
      //     <p className="nodata">
      //       No services / bookings available for the Employee:{" "}
      //       {employeeData.fullName}
      //     </p>
      //   );
      // }

      // const filteredServices = services.filter((service) => {
      //   const matchesId = service.bookingId
      //     .toLowerCase()
      //     .includes(searchBookingId.toLowerCase());
      //   const matchesDate = isWithinDateRange(service.bookingDate);
      //   return matchesId && matchesDate;
      // });

      // return (
      //   <div>
      //     {/* Filters */}
      //     <div className="filter-container">
      //       <label htmlFor="id-filter">Filter by Booking ID: </label>
      //       <input
      //         type="text"
      //         id="id-filter"
      //         value={searchBookingId}
      //         onChange={handleSearchChange1}
      //         placeholder="Enter Booking ID"
      //       />
      //     </div>
      //     <div className="filter-container">
      //       <label>Start Date: </label>
      //       <input
      //         type="date"
      //         value={startDate}
      //         onChange={handleStartDateChange}
      //       />
      //       <label style={{ marginLeft: "8px" }}>End Date: </label>
      //       <input
      //         type="date"
      //         value={endDate}
      //         onChange={handleEndDateChange}
      //       />
      //     </div>

      //     {/* Booking cards */}
      //     {filteredServices.map((service) => (
      //       <div key={service.id} className="service-card">
      //         {/* HEADER – 2–3 items per row */}
      //         <button
      //           type="button"
      //           className="service-card-header"
      //           onClick={() => toggleBooking(service.id)}
      //         >
      //           {/* Row 1 */}
      //           <div className="header-row">
      //             <div className="header-item">
      //               <strong>Booking ID</strong>
      //               <div>{service.bookingId}</div>
      //             </div>
      //             <div className="header-item">
      //               <strong>Date & Slot</strong>
      //               <div>
      //                 {service.bookingDate} {service.timeSlot}
      //               </div>
      //             </div>
      //             <div className="header-item">
      //               <strong>Status</strong>
      //               <div>{service.status}</div>
      //             </div>
      //           </div>

      //           {/* Row 2 */}
      //           <div className="header-row">
      //             <div className="header-item">
      //               <strong>Payment</strong>
      //               <div>
      //                 {service.paymentStatus} ({service.paymentType})
      //               </div>
      //             </div>
      //             <div className="header-item">
      //               <strong>Total</strong>
      //               <div>₹{service.totalPrice}</div>
      //             </div>
      //             <div className="header-item">
      //               <strong>Type</strong>
      //               <div>{service.bookingType}</div>
      //             </div>
      //           </div>
      //         </button>

      //         {/* BODY – expanded details */}
      //         {openBookingId === service.id && (
      //           <div className="service-card-body">
      //             {/* Address & basic info */}
      //             <p>
      //               <strong>Address:</strong>
      //               <span className="address">
      //                 {service.address.split("\n").map((line, index) => (
      //                   <span key={index}>
      //                     {line}
      //                     <br />
      //                   </span>
      //                 ))}
      //               </span>
      //             </p>

      //             <p>
      //               <strong>Pincode:</strong> {service.pincode}
      //             </p>

      //             {service.reason && (
      //               <p>
      //                 <strong>Reason:</strong> {service.reason}
      //               </p>
      //             )}

      //             <p>
      //               <strong>Customer:</strong> {service.user.fullName}{" "}
      //               {service.user.contactNumber &&
      //                 `(${service.user.contactNumber})`}
      //             </p>

      //             {/* TIMELINE ROW – all key times in one row style */}
      //             <div className="timeline-row">
      //               <div className="timeline-item">
      //                 <span className="timeline-label">Confirmed</span>
      //                 <span className="timeline-value">
      //                   {formatTime(service.confirmedAt)}
      //                 </span>
      //               </div>
      //               <div className="timeline-item">
      //                 <span className="timeline-label">Headed</span>
      //                 <span className="timeline-value">
      //                   {formatTime(service.headedToLocation)}
      //                 </span>
      //               </div>
      //               <div className="timeline-item">
      //                 <span className="timeline-label">Reached</span>
      //                 <span className="timeline-value">
      //                   {formatTime(service.reachedLocation)}
      //                 </span>
      //               </div>
      //               <div className="timeline-item">
      //                 <span className="timeline-label">Started</span>
      //                 <span className="timeline-value">
      //                   {formatTime(service.serviceStarted)}
      //                 </span>
      //               </div>
      //               <div className="timeline-item">
      //                 <span className="timeline-label">Finished</span>
      //                 <span className="timeline-value">
      //                   {formatTime(service.serviceFinished)}
      //                 </span>
      //               </div>
      //               <div className="timeline-item">
      //                 <span className="timeline-label">Cancelled</span>
      //                 <span className="timeline-value">
      //                   {formatTime(service.cancelledAt)}
      //                 </span>
      //               </div>
      //             </div>

      //             {/* Order details */}
      //             <div>
      //               <strong>Order Details:</strong>
      //               {service.orderDetail.map((detail) => (
      //                 <div key={detail.id} className="order-detail">
      //                   <p>
      //                     <strong>Service:</strong> {detail.name}
      //                   </p>
      //                   <p>
      //                     <strong>Price:</strong> ₹{detail.price}
      //                   </p>
      //                 </div>
      //               ))}
      //             </div>
      //           </div>
      //         )}
      //       </div>
      //     ))}
      //   </div>
      // );

      case "addInventory":
        return (
          <AddInventoryForm
            onSubmit={handleAddInventory}
            employeeData={employeeData} // optional
          />
        );
      case "unsettledPayments":
        return (
          <UnsettledPayments
            unsettledPayments={unsettledPayments}
            onApprove={handleApprove}
            onApproveAll={handleApproveAll}
            employeeData={employeeData}
          ></UnsettledPayments>
        );
      case "settledPayments":
        return (
          <SettledPayments
            settledPayments={setttledPayments}
            employeeData={employeeData}
          ></SettledPayments>
        );

      case "inventoryList": // for previously added inventories
        // Check if inventorylist array is empty
        return (
          <DatewiseInventory
            datewiseInventoryData={datewiseInventoryData}
            employeeData={employeeData}
          />
        );

      case "inventoryTotal": //current inventory
        return (
          <InventoryTotalSummary
            inventories={inventoryList}
            employeeData={employeeData}
          />
        );

      case "inventoryRequested":
        return (
          <RequestedInventories
            requestedInventories={requestedInventories}
            employeeData={employeeData}
          />
        );
      case "addBlockedSlots":
        console.log("fetchBlockedSlots is");
        return (
          <BlockedSlotManager
            employeeId={employeeId}
            fetchBlockedSlots={fetchBlockedSlots}
            gender={employeeData.gender}
          />
        );
      case "feedbackAnalytics":
        return (
          <EmployeeFeedbackDashboard
            employeeId={employeeId}
          />
        );

      case "fullFinal":
        return <FullFinalSettlement employeeId={employeeId} />;

      case "employeeHistory":
        return (
          <div className="bg-white rounded-xl border p-4">
            <EmployeeChangeHistory employeeId={employeeId} />
          </div>
        );

      default:
        return null;
    }
  };
  return (
    <div className="w-full min-h-screen bg-gray-50 text-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold">
              Employee Dashboard
              {employeeData?.fullName && (
                <span className="text-[#7F58E6]">
                  {" "}
                  ({employeeData.fullName})
                </span>
              )}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Overview of services, payments, inventory and slots for this
              employee.
            </p>
          </div>
        </div>

        {/* MAIN TABS */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
          {Object.entries(TAB_CONFIG).map(([key, tab]) => {
            const isActive = key === activeMainTab;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleMainTabClick(key)}
                className={`px-3 sm:px-4 py-2 text-sm rounded-t-xl border-b-2 transition-colors
                  ${isActive
                    ? "border-[#7F58E6] text-[#7F58E6] bg-white"
                    : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-white"
                  }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* SUB TABS */}
        {currentMainTabConfig?.subTabs?.length > 1 && (
          <div className="flex flex-wrap gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2">
            {currentMainTabConfig.subTabs.map((sub) => {
              const isActive = sub.key === activeSubTab;
              return (
                <button
                  key={sub.key}
                  type="button"
                  onClick={() => handleSubTabClick(sub.key)}
                  className={`px-3 py-1.5 text-xs sm:text-sm rounded-full border transition-colors
                    ${isActive
                      ? "bg-[#7F58E6] text-white border-[#7F58E6]"
                      : "bg-transparent text-gray-600 border-gray-300 hover:bg-gray-100"
                    }`}
                >
                  {sub.label}
                </button>
              );
            })}
          </div>
        )}

        {/* CONTENT CARD */}
        {/* <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5"> */}
        {/* This keeps all your existing logic inside renderCards */}
        {renderCards(activeSubTab)}
        {/* </div> */}
      </div>
    </div>
  );
};

export default EmployeeDashboard;
